import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { computed, autorun } from 'mobx';

import useComponentMounted from '../../../hooks/useComponentMounted';
import User from '../../../Model/User';
import ClientTag from '../../Model/Tag';
import { ClientStatus } from '../../utils/statusFilter';
import PodContext, { usePodFilter } from '../../../context/PodContext';
import SecondaryContextsReadyContext from '../SecondaryContextsReadyContext';
import ActiveClientsContext from '../ActiveClientsContext';
import ManageClientContext, { initialValues } from './ManageClientContext';

const ManageClientContextProvider = ({
  children,
}) => {
  const { params: { userId } } = useRouteMatch();
  const { getFilteredDocs } = usePodFilter();
  const { selectedPodId } = useContext(PodContext);
  const { readyForRemainingContexts } = useContext(SecondaryContextsReadyContext);
  const {
    activeClientDocs,
    isReady: areActiveClientsReady,
  } = useContext(ActiveClientsContext);

  const [clientList, setClientList] = useState(initialValues.clientList);
  const [currentUserId, setCurrentUserId] = useState(userId);
  const [currentPodId, setCurrentPodId] = useState(selectedPodId);
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const isComponentMountedRef = useComponentMounted();
  const [clientSortModel, setClientSortModel] = useState(initialValues.clientSortModel);
  const [selectedTab, setSelectedTab] = useState(initialValues.selectedTab);
  const [clientFilters, setClientFilters] = useState(initialValues.clientFilters);
  const [clientExcludingFilters, setClientExcludingFilters] = useState(initialValues.clientExcludingFilters);
  const [clientTagsFilters, setClientTagsFilters] = useState(initialValues.clientTagsFilters);
  const [baseClientTagsCollection, setBaseClientTagsCollection] = useState(initialValues.baseClientTagsCollection);
  const [customClientTagsCollection, setCustomClientTagsCollection] = useState(
    initialValues.customClientTagsCollection,
  );
  const [clientTagsDocs, setClientTagsDocs] = useState(initialValues.clientTagsDocs);
  const [allUsersLoaded, setAllUsersLoaded] = useState(initialValues.allUsersLoaded);
  const [loadingUsers, setLoadingUsers] = useState(initialValues.loadingUsers);
  const [clientTablePaginationModel, setClientPaginationModel] = useState();
  const [selectedUserType, setSelectedUserType] = useState(initialValues.selectedUserType);

  useEffect(() => {
    if (currentUserId !== userId) {
      setIsReady(false);
    }
  }, [
    currentUserId,
    userId,
  ]);

  useEffect(() => {
    if (currentPodId !== selectedPodId) {
      setIsReady(false);
    }
  }, [currentPodId, selectedPodId]);

  useEffect(() => {
    // Initialize the context only when the primary and secondary contexts are ready, and once the
    // shared active-client roster has loaded.
    if (isReady || !readyForRemainingContexts || !areActiveClientsReady) {
      return;
    }
    setClientList(activeClientDocs);
    setCurrentUserId(userId);
    setCurrentPodId(selectedPodId);
    setLoadingUsers(false);
    setAllUsersLoaded(false);
    setIsReady(true);
  }, [
    activeClientDocs,
    areActiveClientsReady,
    isReady,
    selectedPodId,
    userId,
    readyForRemainingContexts,
  ]);

  useEffect(() => {
    const getClientTags = async () => {
      const baseClientTagsCol = await ClientTag.getClientBaseTags();
      const customClientTagsCol = await ClientTag.getClientCustomTagsforCoach(userId);

      if (isComponentMountedRef.current) {
        setBaseClientTagsCollection(baseClientTagsCol);
        setCustomClientTagsCollection(customClientTagsCol);
      }
    };

    // Initialize the context only when the primary and secondary contexts are ready.
    if (readyForRemainingContexts) {
      getClientTags();
    }
  }, [
    isComponentMountedRef,
    userId,
    readyForRemainingContexts,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      const baseClientTagDocs = baseClientTagsCollection.hasDocs ? baseClientTagsCollection.docs.slice() : [];
      const customClientTagDocs = customClientTagsCollection.hasDocs ? customClientTagsCollection.docs.slice() : [];

      const clientTagDocs = [
        ...baseClientTagDocs,
        ...customClientTagDocs,
      ];

      const clientTagDocsExcludeRemoved = clientTagDocs.filter((tagDoc) => tagDoc.removed !== true);

      // Sort all tags lexicographically.
      clientTagDocsExcludeRemoved.sort((a, b) => a.tag.localeCompare(b.tag));

      if (isComponentMountedRef.current) {
        setClientTagsDocs(clientTagDocsExcludeRemoved);
      }
    });
    return disposer;
  }, [
    baseClientTagsCollection,
    customClientTagsCollection,
    isComponentMountedRef,
  ]);

  const loadAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    const usersByCoachCol = await User.getUsersByCoach(userId);
    if (isComponentMountedRef.current) {
      const filteredUsers = getFilteredDocs(usersByCoachCol.docs, 'id');
      setClientList(filteredUsers);
      setAllUsersLoaded(true);
      setLoadingUsers(false);
    }
  }, [getFilteredDocs, isComponentMountedRef, userId]);

  useEffect(() => {
    // if the client filters are not set to active, we need to load all users
    const shouldLoadAllUsers = (clientFilters.length === 1 && clientFilters[0] !== ClientStatus.ACTIVE)
      || clientFilters.length > 1;
    if (!allUsersLoaded && shouldLoadAllUsers) {
      loadAllUsers();
    }
  }, [
    allUsersLoaded,
    clientFilters,
    loadAllUsers,
  ]);

  // data required for the user table will be calculated at each update to the user/client list
  const clientDataList = useMemo(() => computed(() => clientList
    .map((client) => ({
      id: client.id,
      name: client.name,
      /*
       * Email and phone number are not shown in the table, they are only there to be searched
       * on. Both emails are kept because they differ on secondary multi-coach accounts - the
       * coach may have either the person's real address or the account's shifted one at hand.
       */
      email: client.email,
      accountEmail: client.accountEmail,
      phoneNumber: client.phoneNumber,
      userAvatar: client.avatarUrl,
      clientSince: client.createdAt,
      lastCheckIn: client.lastCheckIn,
      isActive: client.isActive,
      breakEndDate: client.breakEndDate,
      lastWorkout: client.lastWorkoutCompletedAt,
      lastMessageAt: client.lastMessageAt,
      isDelinquent: client.isDelinquent,
      serviceStartAt: client.serviceStartAt,
      postPaymentFormSubmittedAt: client.postPaymentFormSubmittedAt?.toDate(),
      subscriptionStatus: client.subscriptionStatus,
      cancelAt: client.cancelAt,
      amendingAt: client.amendingAt,
      planCode: client.planId,
      productId: client.product,
      productIds: client.activeProductIds,
      monthlyStartAt: client.monthlyStartAt,
      tags: client.tags,
      clientTags: client.customTags || [],
      commitmentEndingDate: client.commitmentEndingDate,
      isAIFriendEnabled: client.isAIFriendEnabled,
      userDoc: client,
    }))), [
    clientList,
  ]).get();

  const contextValue = useMemo(() => ({
    clientDataList,
    isReady,
    clientSortModel,
    setClientSortModel,
    selectedTab,
    setSelectedTab,
    clientFilters,
    setClientFilters,
    clientExcludingFilters,
    setClientExcludingFilters,
    clientTagsDocs,
    clientTagsFilters,
    setClientTagsFilters,
    loadingUsers,
    clientTablePaginationModel,
    setClientPaginationModel,
    selectedUserType,
    setSelectedUserType,
  }), [
    clientDataList,
    isReady,
    clientSortModel,
    setClientSortModel,
    selectedTab,
    setSelectedTab,
    clientFilters,
    setClientFilters,
    clientExcludingFilters,
    setClientExcludingFilters,
    clientTagsDocs,
    clientTagsFilters,
    setClientTagsFilters,
    loadingUsers,
    clientTablePaginationModel,
    setClientPaginationModel,
    selectedUserType,
    setSelectedUserType,
  ]);

  return (
    <ManageClientContext.Provider value={contextValue}>
      {children}
    </ManageClientContext.Provider>
  );
};

ManageClientContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ManageClientContextProvider);
